import { useState, useEffect } from "react";

// Google Input Tools language mapping
const ITC_MAP = {
  mr: "mr-t-i0-und",
  hi: "hi-t-i0-und",
  bn: "bn-t-i0-und",
  gu: "gu-t-i0-und",
  kn: "kn-t-i0-und",
  ml: "ml-t-i0-und",
  pa: "pa-t-i0-und",
  ta: "ta-t-i0-und",
  te: "te-t-i0-und",
  ur: "ur-t-i0-und",
  or: "or-t-i0-und",
};

// In-memory cache to avoid duplicate network calls
const transliterationCache = new Map();

/**
 * Transliterates text into the target Indic language script using Google Input Tools API
 * with synchronous caching and fallback.
 */
export async function transliterateText(text, lang) {
  if (!text || typeof text !== "string") return "";
  const trimmed = text.trim();
  if (!trimmed || lang === "en" || !ITC_MAP[lang]) return trimmed;

  // If text already contains non-ASCII characters, assume already in native script
  if (/[^\u0000-\u007F]/.test(trimmed)) return trimmed;

  const cacheKey = `${lang}:${trimmed.toLowerCase()}`;
  if (transliterationCache.has(cacheKey)) {
    return transliterationCache.get(cacheKey);
  }

  // Check localStorage cache
  try {
    const localCached = localStorage.getItem(`grgpt_translit_${cacheKey}`);
    if (localCached) {
      transliterationCache.set(cacheKey, localCached);
      return localCached;
    }
  } catch (e) {
    // Ignore storage errors
  }

  try {
    const itc = ITC_MAP[lang];
    const url = `https://inputtools.google.com/request?text=${encodeURIComponent(trimmed)}&itc=${itc}&num=1`;
    const response = await fetch(url);
    const data = await response.json();

    if (data && data[0] === "SUCCESS" && data[1]?.[0]?.[1]?.[0]) {
      const transliterated = data[1][0][1][0];
      transliterationCache.set(cacheKey, transliterated);
      try {
        localStorage.setItem(`grgpt_translit_${cacheKey}`, transliterated);
      } catch (e) {}
      return transliterated;
    }
  } catch (err) {
    console.warn("Transliteration request failed, using original text:", err);
  }

  return trimmed;
}

/**
 * React hook to get a transliterated version of a name in the selected UI language.
 */
export function useTransliteratedName(name, lang) {
  const firstName = name ? name.trim().split(" ")[0] : "";
  const cacheKey = `${lang}:${firstName.toLowerCase()}`;
  
  const getInitialValue = () => {
    if (!firstName || lang === "en" || !ITC_MAP[lang]) return firstName;
    if (transliterationCache.has(cacheKey)) return transliterationCache.get(cacheKey);
    try {
      const localCached = localStorage.getItem(`grgpt_translit_${cacheKey}`);
      if (localCached) {
        transliterationCache.set(cacheKey, localCached);
        return localCached;
      }
    } catch (e) {}
    return firstName;
  };

  const [transliterated, setTransliterated] = useState(getInitialValue);

  useEffect(() => {
    if (!firstName || lang === "en" || !ITC_MAP[lang]) {
      setTransliterated(firstName);
      return;
    }

    let isMounted = true;
    transliterateText(firstName, lang).then((res) => {
      if (isMounted && res) {
        setTransliterated(res);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [firstName, lang]);

  return transliterated;
}

export const GREETINGS_BY_LANG = {
  mr: { morning: "शुभ प्रभात", afternoon: "शुभ दुपार", evening: "शुभ संध्याकाळ" },
  hi: { morning: "सुप्रभात", afternoon: "शुभ दोपहर", evening: "शुभ संध्या" },
  bn: { morning: "সুপ্রভাত", afternoon: "শুভ অপরাহ্ন", evening: "শুভ সন্ধ্যা" },
  te: { morning: "శుభోదయం", afternoon: "శుభ మధ్యాహ్నం", evening: "శుభ సాయంత్రం" },
  ta: { morning: "காலை வணக்கம்", afternoon: "மதிய வணக்கம்", evening: "மாலை வணக்கம்" },
  gu: { morning: "શુભ સવાર", afternoon: "શુભ બપોર", evening: "શુભ સાંજ" },
  kn: { morning: "ಶುಭೋದಯ", afternoon: "ಶುಭ ಅಪರಾಹ್ನ", evening: "ಶುಭ ಸಂಜೆ" },
  ml: { morning: "സുപ്രഭാതം", afternoon: "ശുഭ ഉച്ചതിരിഞ്ഞ്", evening: "ശുഭ സായാഹ്നം" },
  pa: { morning: "ਸੁਵੱਖਤੇ", afternoon: "ਸ਼ੁਭ ਦੁਪਹਿਰ", evening: "ਸ਼ਾਮ ਸੁਖਾਵੀਂ" },
  ur: { morning: "صبح بخیر", afternoon: "دوپہر بخیر", evening: "شام بخیر" },
  or: { morning: "ସୁପ୍ରଭାତ", afternoon: "ଶୁଭ ଅପରାହ୍ନ", evening: "ଶୁଭ ସନ୍ଧ୍ୟା" },
  en: { morning: "Good morning", afternoon: "Good afternoon", evening: "Good evening" },
};

export const getTimeBasedGreeting = (name, lang = "en") => {
  const hour = new Date().getHours();
  let timePeriod = "morning";
  if (hour >= 12 && hour < 17) {
    timePeriod = "afternoon";
  } else if (hour >= 17 || hour < 5) {
    timePeriod = "evening";
  }

  const langGreetings = GREETINGS_BY_LANG[lang] || GREETINGS_BY_LANG.en;
  const greetingText = langGreetings[timePeriod] || `Good ${timePeriod}`;

  if (!name) return greetingText;
  
  if (lang === "ur") {
    return `${greetingText}، ${name}`;
  }
  return `${greetingText}, ${name}`;
};
